import React from 'react';
import {
    Drawer,
    Box,
    IconButton,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageSwitcher from './LanguageSwitcher';
import NavigationList from './NavigationList';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetNavigation } from '../../../hooks/storefront/useGetNavigation';
import { useGetPages } from '../../../hooks/storefront/useGetPages';

const MobileDrawer = ({ open, onClose, isAuthenticated }) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const lng = i18n.language || "en";

    // جلب بيانات القائمة الرئيسية من الـ API
    const { isLoading: navigationLoading, navigation } = useGetNavigation();
    const { pages } = useGetPages();

    const go = (path) => {
        navigate(path);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.background.paper,
                }
            }}
        >
            <Box sx={{ width: 280, p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            flexGrow: 1,
                            color: theme.palette.text.primary,
                        }}
                    >
                        {t('navigation.menu')}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                {/* Navigation */}
                <NavigationList
                    navigation={navigation?.data}
                    navigationLoading={navigationLoading}
                    onNavigate={go}
                />

                <Divider />

                {/* Dynamic Pages */}
                <List>
                    <ListItemButton onClick={() => go('/contact')}>
                        <ListItemText
                            primary={t('navigation.contact', 'Contact Us')}
                            primaryTypographyProps={{
                                color: theme.palette.text.primary,
                                variant: 'body2',
                            }}
                        />
                    </ListItemButton>
                    <ListItemButton onClick={() => go('/blog')}>
                        <ListItemText
                            primary={t('blog.blog', 'Blog')}
                            primaryTypographyProps={{
                                color: theme.palette.text.primary,
                                variant: 'body2',
                            }}
                        />
                    </ListItemButton>
                    {pages?.map((page) => (
                        <ListItemButton key={page.id} onClick={() => go(`/page/${page.slug}`)}>
                            <ListItemText
                                primary={page.title?.[lng] || page.title?.en || page.title?.ar || ""}
                                primaryTypographyProps={{
                                    color: theme.palette.text.primary,
                                    variant: 'body2',
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>

                <Divider />

                {/* Auth */}
                <List>
                    {!isAuthenticated ? (
                        <>
                            <ListItemButton onClick={() => go('/login')}>
                                <ListItemText
                                    primary={t('navigation.login')}
                                    primaryTypographyProps={{
                                        color: theme.palette.text.primary,
                                    }}
                                />
                            </ListItemButton>
                            <ListItemButton onClick={() => go('/register')}>
                                <ListItemText
                                    primary={t('navigation.register')}
                                    primaryTypographyProps={{
                                        color: theme.palette.text.primary,
                                    }}
                                />
                            </ListItemButton>
                        </>
                    ) : (
                        <>
                            <ListItemButton onClick={() => go('/profile')}>
                                <ListItemText
                                    primary={t('navigation.profile')}
                                    primaryTypographyProps={{
                                        color: theme.palette.text.primary,
                                    }}
                                />
                            </ListItemButton>
                            <ListItemButton onClick={() => go('/orders')}>
                                <ListItemText
                                    primary={t('navigation.orders')}
                                    primaryTypographyProps={{
                                        color: theme.palette.text.primary,
                                    }}
                                />
                            </ListItemButton>
                            <Divider sx={{ my: 1 }} />
                            <ListItemButton>
                                <ListItemText
                                    primary={t('navigation.logout')}
                                    primaryTypographyProps={{
                                        color: theme.palette.text.primary,
                                    }}
                                />
                            </ListItemButton>
                        </>
                    )}
                </List>

                <Divider sx={{ my: 2 }} />

                {/* Language */}
                <LanguageSwitcher />
            </Box>
        </Drawer>
    );
};

export default MobileDrawer;
